import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import configServ from "../../../services/config";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import DispatchScheduleItems from "./DispatchScheduleItems";
import {
  getDailyTargetQty,
  getDefaultDispatchDateRange,
  getDispatchDaysCount,
} from "../../../utility/dispatchScheduleDates";

const emptyItemRow = () => ({
  item_id: "",
  itemname: "",
  item_code: "",
  qty: "",
  rate: "",
  order_id: "",
  order_no: "",
});

const DispatchScheduleCard = ({
  isCreate,
  setIsCreate,
  isChanged,
  setIsChanged,
}) => {
  const dispatch = useDispatch();
  const { company_id, user_id, is_admin, is_super, permissions } = useSelector(
    (state) => state.GlobalVariables
  );

  const defaultDates = getDefaultDispatchDateRange();
  const [customers, setCustomers] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [startDate, setStartDate] = useState(defaultDates.start_date);
  const [endDate, setEndDate] = useState(defaultDates.end_date);
  const [itemList, setItemList] = useState([emptyItemRow()]);
  const [errors, setErrors] = useState({});

  const canCreate = is_super || is_admin || permissions?.includes(79);

  const totalQty = useMemo(
    () =>
      itemList.reduce((sum, row) => sum + (Number(row.qty) || 0), 0),
    [itemList]
  );

  const scheduleDays = getDispatchDaysCount(startDate, endDate);
  const dailyTarget = getDailyTargetQty(totalQty, startDate, endDate);

  useEffect(() => {
    if (isCreate) {
      fetchCustomers();
    }
  }, [isCreate, company_id]);

  useEffect(() => {
    if (customer?.id) {
      fetchCustomerItems(customer.id);
    } else {
      setItemOptions([]);
    }
  }, [customer?.id, company_id]);

  const fetchCustomers = async () => {
    try {
      const res = await configServ.getDispatchScheduleCustomers({ company_id });
      if (res.status === 200) {
        setCustomers(res.data || []);
      } else {
        setCustomers([]);
      }
    } catch (e) {
      console.log(e);
      setCustomers([]);
    }
  };

  const fetchCustomerItems = async (customerId) => {
    try {
      dispatch(triggerLoader());
      const res = await configServ.getDispatchScheduleCustomerItems({
        company_id,
        customer_id: customerId,
      });
      if (res.status === 200) {
        setItemOptions(res.data || []);
      } else {
        setItemOptions([]);
      }
    } catch (e) {
      console.log(e);
      setItemOptions([]);
    } finally {
      dispatch(triggerLoader());
    }
  };

  const handleReset = () => {
    const dates = getDefaultDispatchDateRange();
    setCustomer(null);
    setStartDate(dates.start_date);
    setEndDate(dates.end_date);
    setItemList([emptyItemRow()]);
    setItemOptions([]);
    setErrors({});
  };

  const handleAddItem = () => {
    setItemList((prev) => [...prev, emptyItemRow()]);
  };

  const handleDeleteItem = (index) => {
    setItemList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setItemList((prev) => {
      const next = [...prev];
      if (field === "item") {
        next[index] = {
          ...next[index],
          item_id: value?.id || "",
          itemname: value?.itemname || "",
          item_code: value?.item_code || "",
          qty: value?.qty ?? "",
          rate: value?.rate ?? "",
          order_id: value?.order_id ?? "",
          order_no: value?.order_no ?? "",
        };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!customer?.id) {
      nextErrors.customer = "Customer is required";
    }
    if (!startDate) {
      nextErrors.start_date = "Start date is required";
    }
    if (!endDate) {
      nextErrors.end_date = "End date (target dispatch date) is required";
    }
    if (startDate && endDate && endDate < startDate) {
      nextErrors.end_date = "End date must be on or after start date";
    }

    const usedItems = new Set();
    itemList.forEach((row, index) => {
      if (!row.item_id) {
        nextErrors[`item_${index}`] = "Item is required";
      } else if (usedItems.has(String(row.item_id))) {
        nextErrors[`item_${index}`] = "Duplicate item";
      } else {
        usedItems.add(String(row.item_id));
      }

      if (!row.qty || Number(row.qty) <= 0) {
        nextErrors[`qty_${index}`] = "Valid quantity is required";
      }

      if (row.rate === "" || row.rate === null || Number(row.rate) < 0) {
        nextErrors[`rate_${index}`] = "Valid rate is required";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!canCreate) {
      toast.error("You do not have permission to create dispatch schedule.");
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the form errors.");
      return;
    }

    try {
      dispatch(triggerLoader());
      const payload = {
        company_id,
        user_id,
        customer_id: customer.id,
        customer_name: customer.contact_name || customer.fname,
        start_date: startDate,
        end_date: endDate,
        items: itemList.map((row) => ({
          item_id: row.item_id,
          itemname: row.itemname,
          item_code: row.item_code,
          qty: row.qty,
          rate: row.rate,
          order_id: row.order_id,
          order_no: row.order_no,
        })),
      };

      const res = await configServ.createDispatchSchedule(payload);
      if (res.status === 200) {
        toast.success(res.message || "Dispatch schedule saved.");
        handleReset();
        setIsCreate(false);
        setIsChanged(!isChanged);
      } else {
        toast.error(res.message || "Failed to save dispatch schedule.");
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to save dispatch schedule.");
    } finally {
      dispatch(triggerLoader());
    }
  };

  if (!isCreate) {
    return null;
  }

  const actionButtons = (
    <Box display="flex" gap={1} flexWrap="wrap">
      <Button
        variant="contained"
        color="success"
        startIcon={<SaveIcon />}
        onClick={handleSubmit}
        disabled={!canCreate}
      >
        Add
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<RestartAltIcon />}
        onClick={handleReset}
      >
        Reset
      </Button>
    </Box>
  );

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        {actionButtons}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) =>
              option.contact_name || option.fname || ""
            }
            value={customer}
            onChange={(event, newValue) => {
              setCustomer(newValue);
              setItemList([emptyItemRow()]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                error={Boolean(errors.customer)}
                helperText={errors.customer || ""}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Start Date (Invoice from)"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors.start_date)}
            helperText={
              errors.start_date ||
              "Daily invoice creation begins from this date"
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="End Date (Target dispatch)"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors.end_date)}
            helperText={
              errors.end_date ||
              "Full order quantity should be dispatched by this date"
            }
          />
        </Grid>
        {totalQty > 0 && scheduleDays > 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Schedule spans {scheduleDays} day(s). Total quantity {totalQty} →
              approx. {dailyTarget} qty/day invoice target across all items.
            </Typography>
          </Grid>
        )}
      </Grid>

      <DispatchScheduleItems
        itemList={itemList}
        itemOptions={itemOptions}
        handleItemChange={handleItemChange}
        handleAddItem={handleAddItem}
        handleDeleteItem={handleDeleteItem}
        errors={errors}
        startDate={startDate}
        endDate={endDate}
      />

      <Box display="flex" justifyContent="flex-end" mt={2}>
        {actionButtons}
      </Box>
    </Paper>
  );
};

export default memo(DispatchScheduleCard);
